// import node module libraries
import React from "react";
import {
  FieldErrors,
  FieldValues,
  UseFormRegister,
  Path,
} from "react-hook-form";
import { cn } from "@/lib/utils";

// import custom components
import { Label } from "./label";

interface InputProps<T extends FieldValues = FieldValues> {
  id: Path<T>;
  type?: string;
  disabled?: boolean;
  required?: boolean;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  className?: string;
  placeholder?: string;
  errorMsg?: string; // Custom error message
  name?: string;
  label?: string;
  icon?: React.ReactNode;
  autoComplete?: string;
  rows?: number;
  as?: string;
  defaultValue?: string;
  pattern?: RegExp; // Optional custom validation pattern
  minLength?: number; // Optional minimum length validation
  maxLength?: number; // Optional maximum length validation
  readOnly?: boolean;
}

const CustomInput = <T extends FieldValues = FieldValues>({
  id,
  type = "text",
  disabled,
  register,
  required,
  errors,
  className,
  placeholder,
  errorMsg,
  name,
  label,
  icon,
  autoComplete,
  rows,
  as,
  defaultValue,
  pattern,
  minLength,
  maxLength,
  readOnly,
  ...props
}: InputProps<T>) => {
  // Combine validation rules into a single object
  const validationRules: Record<string, any> = {
    required: required ? `${label || "This field"} is required` : false,
    minLength: minLength
      ? { value: minLength, message: `Minimum length is ${minLength}` }
      : undefined,
    maxLength: maxLength
      ? { value: maxLength, message: `Maximum length is ${maxLength}` }
      : undefined,
    // Only add the pattern rule if it's provided
    ...(pattern && {
      pattern: { value: pattern, message: errorMsg || "Invalid format" },
    }),
  };

  // Filter out undefined values in validationRules
  const filteredValidationRules = Object.fromEntries(
    Object.entries(validationRules).filter(([_, v]) => v !== undefined)
  );

  const error = errors[id];

  // Check for specific error types
  const isMinLengthError = error?.type === "minLength";
  const isMaxLengthError = error?.type === "maxLength";
  const isRequiredError = error?.type === "required";
  const isPatternError = error?.type === "pattern";

  return (
    <div className="w-full relative">
      {label && (
        <Label
          htmlFor={id}
          className="font-medium text-black dark:text-neutral-500"
        >
          {label}
        </Label>
      )}
      <div className="relative">
        {as === "textarea" ? (
          <textarea
            id={id}
            disabled={disabled}
            {...register(id, filteredValidationRules)}
            placeholder={placeholder}
            className={cn(
              `dark:bg-transparent flex w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-neutral-500 placeholder:text-sm  focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-neutral-950 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 ${
                error ? "focus-visible:ring-rose-500" : ""
              }`,
              className
            )}
            name={name}
            autoComplete={autoComplete}
            rows={rows}
            defaultValue={defaultValue}
            {...props}
          />
        ) : (
          <input
            id={id}
            disabled={disabled}
            {...register(id, filteredValidationRules)}
            placeholder={placeholder}
            type={type}
            className={cn(
              `flex h-11 w-full rounded-md border dark:border-neutral-700 bg-transparent px-3 py-2 text-base font-normal ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-neutral-700 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-neutral-950 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-sm
              `,
              className
            )}
            name={name}
            autoComplete={autoComplete}
            defaultValue={defaultValue}
            readOnly={readOnly}
            {...props}
          />
        )}

        {/* Display error messages below input */}

        {error && (
          <p className="absolute capitalize text-xs text-rose-500 mt-1">
            {error.message?.toString()}
          </p>
        )}

        <div className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-neutral-500">
          {icon}
        </div>
      </div>
    </div>
  );
};

export default CustomInput;
